import React from 'react'
import PropTypes from 'prop-types'
import { Header, Modal, TextArea, Segment } from 'semantic-ui-react'
import * as R from 'ramda'
import styled from 'styled-components'

import { embedScript, generateSatisfactionRatingLinkWithoutRating } from './utils'
import IFrame from 'components/IFrame'
import SurveyPublishDescription from 'components/SurveyPublishDescription'
import auth from 'auth'

const GenerateEmbedCodeButton = ({ handleOnClick, className }) => {
  return (
    <a className={`ui huge primary generate-embed button ${className}`} onClick={handleOnClick}>
      Generate embed code
    </a>
  )
}

GenerateEmbedCodeButton.propTypes = {
  handleOnClick: PropTypes.func.isRequired,
  className: PropTypes.string,
}

const StyledTextArea = styled(TextArea)`
  width: 100%;
  font-family: 'Lato';
`

export default class GenerateEmbedCodeModal extends React.Component {
  static propTypes = {
    surveyToken: PropTypes.string,
    description: PropTypes.string,
    linkText: PropTypes.string,
    linkUrl: PropTypes.string,
    isVisualToggleShown: PropTypes.bool,
    currentSelectedIntegration: PropTypes.string,
    primaryQuestion: PropTypes.object,
    className: PropTypes.string,
    ratingIconDimension: PropTypes.object,
    embedStyleType: PropTypes.string,
    ratingIconDisplayOptions: PropTypes.object,
    placeholders: PropTypes.object,
    emailPreviewIframeHeight: PropTypes.number,
    customField: PropTypes.shape({
      ticket: PropTypes.object,
      teamMember: PropTypes.object,
    }),
    onUpdateGenericEmbedPlaceholdersAndCustomFields: PropTypes.func,
  }

  static defaultProps = {
    isVisualToggleShown: false,
  }

  state = {
    isModalOpen: false,
    copySurveyButtonText: 'Copy survey',
    embedCodeType: 'visual',
  }

  showModal = (isModalOpen, isVisualToggleShown) => {
    this.setState({
      isModalOpen,
      embedCodeType: isVisualToggleShown ? 'visual' : 'html',
      copySurveyButtonText: 'Copy survey',
    })
  }

  handleToggleEmbedCodeType = (embedCodeType) => {
    this.setState({
      embedCodeType,
      copySurveyButtonText: 'Copy survey',
    })
  }

  selectAndCopy = (embedCodeType) => {
    if (embedCodeType === 'html' || embedCodeType === 'link') {
      const embed = document.getElementById('embedArea')
      embed.select()
      document.execCommand('copy')
    } else {
      const iframe = document.querySelectorAll('#embedArea iframe')[0]
      const iframeDocument = iframe.contentWindow.document
      const range = iframeDocument.createRange()
      range.selectNodeContents(iframeDocument)
      const selection = iframe.contentWindow.getSelection()
      selection.removeAllRanges()
      selection.addRange(range)
      iframeDocument.execCommand('copy')
    }

    this.setState({
      copySurveyButtonText: 'Copied!',
    })
  }

  handleGenerateEmbedCode = () => {
    this.showModal(true, this.props.isVisualToggleShown)

    if (this.props.currentSelectedIntegration === 'generic_embed') {
      this.props.onUpdateGenericEmbedPlaceholdersAndCustomFields()
    }
  }

  onClickEmbedCodeArea = (e) => {
    e.target.select()
  }

  render() {
    return (
      <React.Fragment>
        <Modal
          trigger={
            auth.isDemoUser() ? null : (
              <GenerateEmbedCodeButton
                handleOnClick={this.handleGenerateEmbedCode}
                className={this.props.className}
              />
            )
          }
          size="small"
          open={this.state.isModalOpen}
          onClose={R.partial(this.showModal, [false, this.props.isVisualToggleShown])}
        >
          <Header>
            <div className="header">
              Copy your embed code
              <div className="ui mini basic buttons right floated">
                {this.props.isVisualToggleShown && (
                  <a
                    className={`ui button ${this.state.embedCodeType === 'visual' ? 'active' : ''}`}
                    onClick={R.partial(this.handleToggleEmbedCodeType, ['visual'])}
                  >
                    <i className="icon far fa-images" />
                    Visual
                  </a>
                )}
                <a
                  className={`ui button ${this.state.embedCodeType === 'html' ? 'active' : ''}`}
                  onClick={R.partial(this.handleToggleEmbedCodeType, ['html'])}
                >
                  <i className="icon far fa-code" />
                  HTML
                </a>
                <a
                  className={`ui button ${this.state.embedCodeType === 'link' ? 'active' : ''}`}
                  onClick={R.partial(this.handleToggleEmbedCodeType, ['link'])}
                >
                  <i className="icon far fa-link" />
                  Link
                </a>
              </div>
            </div>
          </Header>
          <Modal.Content>
            <div className="description">
              <SurveyPublishDescription
                content={this.props.description}
                linkText={this.props.linkText}
                linkUrl={this.props.linkUrl}
              />
              <Segment padded={this.state.embedCodeType === 'visual' ? 'very' : true}>
                {this.renderEmbedCodeContent(this.state.embedCodeType)}
              </Segment>
              <a
                className="ui fluid large primary button"
                onClick={R.partial(this.selectAndCopy, [this.state.embedCodeType])}
              >
                {this.state.copySurveyButtonText}
              </a>
            </div>
          </Modal.Content>
        </Modal>
      </React.Fragment>
    )
  }

  renderEmbedCodeContent = (embedCodeType) => {
    const surveyConfig = {
      surveyToken: this.props.surveyToken,
      primaryQuestion: this.props.primaryQuestion,
      currentSelectedIntegration: this.props.currentSelectedIntegration,
    }

    const htmlEmbedCode = embedScript(
      surveyConfig,
      this.props.placeholders,
      this.props.embedStyleType,
      this.props.ratingIconDisplayOptions,
      this.props.ratingIconDimension,
      this.props.customField
    )

    if (embedCodeType === 'visual') {
      return (
        <div id="embedArea">
          <IFrame
            height={this.props.emailPreviewIframeHeight}
            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          >
            <div
              id="iframe-content"
              dangerouslySetInnerHTML={{
                __html: htmlEmbedCode,
              }}
            />
          </IFrame>
        </div>
      )
    }

    if (embedCodeType === 'html') {
      return (
        <div className="ui form" style={{ wordWrap: 'break-word' }}>
          <div className="field">
            <StyledTextArea
              id="embedArea"
              rows={10}
              onClick={this.onClickEmbedCodeArea}
              spellCheck={false}
              value={htmlEmbedCode}
            />
          </div>
        </div>
      )
    }

    if (embedCodeType === 'link') {
      return (
        <div className="ui form" style={{ wordWrap: 'break-word' }}>
          <div className="field">
            <StyledTextArea
              id="embedArea"
              rows={10}
              onClick={this.onClickEmbedCodeArea}
              spellCheck={false}
              value={generateSatisfactionRatingLinkWithoutRating(
                this.props.surveyToken,
                this.props.placeholders,
                this.props.currentSelectedIntegration,
                this.props.ratingIconDisplayOptions.isRemovePlaceholder,
                this.props.customField
              )}
            />
          </div>
        </div>
      )
    }
  }
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/GenerateEmbedCodeModal/index.js